<template>
  <div class="bi-navigation" :class="{ 'has-main-action': showsMainAction }">
    <div class="__container">
      <router-link exact-active-class="active" class="__navigation" :to="paths.businessIntelligenceDashboard">
        <span>{{ $t('page.business-intelligence.navigation.performance') }}</span>
      </router-link>
      <router-link
        exact-active-class="active"
        class="__navigation left-border"
        :to="paths.businessIntelligenceCompetition"
      >
        <span>{{ $t('page.business-intelligence.navigation.competition') }}</span>
      </router-link>
      <span class="__navigation-container" v-if="$route.params.competitorId">
        <BaseIcon class="icon" name="chevron-right" />
        <router-link
          class="__navigation active"
          :to="{
            name: 'business-intelligence-competitor-performance',
            params: { competitorId: $route.params.competitorId },
          }"
        >
          <BaseLoadingValue :state-loading="!competitorName">
            <template slot="content">
              <span class="__breadcrumb-name" :title="competitorName">{{ competitorName }}</span>
            </template>
          </BaseLoadingValue>
        </router-link>
      </span>
      <router-link
        exact-active-class="active"
        class="__navigation left-border"
        :to="paths.businessIntelligenceEmitters"
      >
        <span>{{ $t('page.business-intelligence.navigation.emitters') }}</span>
      </router-link>
      <span class="__navigation-container" v-if="$route.params.emitterId">
        <BaseIcon class="icon" name="chevron-right" />
        <router-link
          class="__navigation active"
          :to="{
            name: 'business-intelligence-emitter-performance',
            params: { emitterId: $route.params.emitterId },
          }"
        >
          <BaseLoadingValue :state-loading="!emitterName">
            <template slot="content">
              <span class="__breadcrumb-name" :title="emitterName">{{ emitterName }}</span>
            </template>
          </BaseLoadingValue>
        </router-link>
      </span>
      <router-link
        class="__navigation left-border"
        v-if="tenantHasBiAccess()"
        exact-active-class="active"
        :to="paths.businessIntelligenceGraph"
      >
        <span>{{ $t('page.business-intelligence.navigation.graph') }}</span>
      </router-link>
    </div>
    <!-- Phones: the page's main action, as a "+" at the end of the sections' line. -->
    <BaseButton v-if="showsMainAction" class="__main-action" has-icon-left @click.native="mainAction.run()">
      <BaseIcon :name="mainAction.icon" />
      <span class="__main-action-label">{{ mainAction.label }}</span>
    </BaseButton>
    <div class="__nav-right-section">
      <BaseIcon class="icon" name="info" />
      <div class="__nav-right-section-content">
        <div class="__nav-right-section--title">
          {{ $t('menu.business-intelligence') }}

          <BaseTag tone="info" preset="primary">
            {{ $t('tags.new') }}
          </BaseTag>
        </div>
        <div class="__nav-right-section--subtitle">
          {{ $t('page.business-intelligence.navigation.data-source-info') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { paths } from '@/util/paths';
import { tenantHasBiAccess } from '@/util/module-access-util';
import { viewport } from '@/util/viewport';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';

export default {
  name: 'BINavigation',
  components: {
    BaseButton,
    BaseIcon,
    BaseTag,
    BaseLoadingValue,
  },
  props: {
    active: {
      type: String,
      default: 'performance',
    },
    // The page's main action, from PageMainAction: `{ label, icon, run }`.
    mainAction: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['competitorPerformanceSummary', 'emitter', 'emitterPerformanceSummary']),
    paths() {
      return paths;
    },
    showsMainAction() {
      return !!this.mainAction && viewport.size === 'phone';
    },
    competitorName() {
      // get from route if available, otherwise from store after loading the summary
      return this.competitorPerformanceSummary && this.competitorPerformanceSummary.competitor
        ? this.competitorPerformanceSummary.competitor.name
        : this.$route.query.name
        ? this.$route.query.name
        : undefined;
    },
    emitterName() {
      // the summary or the emitter once loaded, else the name passed along by the list
      if (this.emitterPerformanceSummary && this.emitterPerformanceSummary.emitter) {
        return this.emitterPerformanceSummary.emitter.name;
      }
      if (this.emitter && this.emitter.id === this.$route.params.emitterId) {
        return this.emitter.name;
      }
      return this.$route.query.name ? this.$route.query.name : undefined;
    },
  },
  watch: {
    // Phones: the sections' line scrolls sideways; the section shown comes into sight, a competitor's
    // or an emitter's once its name is known, and the page's "+" leaves the line less room.
    competitorName: {
      handler() {
        this.$nextTick(this.showActive);
      },
      immediate: true,
    },
    emitterName() {
      this.$nextTick(this.showActive);
    },
    '$route.path'() {
      this.$nextTick(this.showActive);
    },
    showsMainAction() {
      this.$nextTick(this.showActive);
    },
  },
  mounted() {
    // Its words change width once the page's font has loaded.
    if (document.fonts) document.fonts.ready.then(() => this.showActive());
  },
  methods: {
    tenantHasBiAccess,
    showActive() {
      const line = this.$el && this.$el.querySelector('.__container');
      const active = line && line.querySelector('.__navigation.active');
      if (!active) return;
      const lineBox = line.getBoundingClientRect();
      const box = active.getBoundingClientRect();
      if (box.right > lineBox.right) line.scrollLeft += box.right - lineBox.right;
      else if (box.left < lineBox.left) line.scrollLeft -= lineBox.left - box.left;
    },
  },
};
</script>

<style lang="scss">
.bi-navigation {
  display: flex;
  align-items: center;
  position: sticky;
  background: $color-white;
  box-shadow: $shadow-dropdown;
  height: $header-height;
  top: $menu-top-bar-height;
  z-index: $header-index;
  justify-content: space-between;
  padding-right: 32px;

  .__container {
    display: flex;
    font-size: $font-size-small;
    min-width: 0;

    .icon {
      // a long breadcrumb name must not squeeze the chevron
      flex-shrink: 0;
      width: 14px;
      min-width: 14px;
    }

    .__navigation-container {
      display: flex;
      align-items: center;
      margin-left: 16px;
      min-width: 0;

      // let the name shrink, then cut it with an ellipsis (full name in the tooltip)
      .__navigation {
        min-width: 0;

        > div {
          min-width: 0;
        }
      }

      .__breadcrumb-name {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .__navigation {
      display: flex;
      align-items: center;
      color: $color-normal-pale;
      padding: 0 32px;
      text-decoration: none;
      font-family: $font-family-raleway;
      font-size: $font-size-page-title;
      text-transform: uppercase;

      &.active {
        color: $color-normal;
      }

      &:hover:not(.active):not(.disabled) {
        color: $color-accent;
      }

      &.left-border {
        border-left: 1px solid $color-border;
      }

      &.disabled {
        color: $color-normal-pale;
      }
    }
  }

  .__nav-right-section {
    display: flex;
    gap: 16px;
    flex-direction: row;
    align-items: center;
    border-left: 1px solid $color-border;
    height: $header-height;
    padding-left: 24px;

    .base-icon {
      width: 32px;
      height: 32px;
      color: $color-normal-pale;
    }
  }

  .__nav-right-section-content {
    display: flex;
    flex-direction: column;
    max-width: 480px;

    .__nav-right-section {
      &--title {
        font-weight: bold;
        display: flex;
        align-items: center;
        gap: 4px;
        margin-bottom: 4px;
      }

      &--subtitle {
        font-size: $font-size-xsmall;
        color: $color-normal-pale;
      }
    }
  }

  @media screen and (max-width: 1700px) {
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 32px;

    .__container {
      padding: 16px 8px;
      min-height: 32px;
      align-items: flex-start;

      .__navigation:first-child {
        padding-left: 0;
      }
    }

    .__nav-right-section {
      min-height: $header-height;
      height: max-content;
      padding: 8px;
      border-left: none;
      border-top: 1px solid $color-border;
      width: 100%;
    }
  }

  // Phones: the sections on one line that scrolls sideways, the current one underlined, and the
  // header scrolling away with the page, as the other pages' do.
  @include phone-page-header {
    padding: 0 $phone-page-gutter;
  }

  @include screen-below('tablet') {
    .__container {
      align-self: stretch;
      align-items: stretch;
      max-width: 100%;
      margin: 0 (-$phone-page-gutter);
      padding: 0 $phone-page-gutter;
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      // The sections are the page's title: the line is the 56px of every phone header.
      .__navigation {
        flex: none;
        min-height: $phone-page-header-height;
        padding: 0 12px;
        border-bottom: 2px solid transparent;
        font-size: 16px;
        white-space: nowrap;

        &.left-border {
          border-left: none;
        }

        &.active {
          border-bottom-color: $color-accent;
        }
      }

      // A competitor's or an emitter's name in full: the line scrolls, where the larger screens
      // shrink it to an ellipsis (its min-width: 0 let it fall to nothing, under the next section).
      .__navigation-container {
        flex: none;
      }

      .__navigation-container .__navigation {
        padding-left: 4px;
      }
    }

    .__nav-right-section {
      gap: 8px;
      padding: 8px 0;

      .base-icon {
        flex: none;
        width: 20px;
        height: 20px;
      }
    }

    // The page's main action (PageMainAction) ends the sections' line, which scrolls beside it.
    &.has-main-action {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      column-gap: 8px;

      .__container {
        margin-right: 0;
        padding-right: 0;
      }

      .__nav-right-section {
        grid-column: 1 / -1;
      }
    }
  }

  .__main-action {
    @include phone-main-action;
  }

  .__main-action-label {
    @include visually-hidden;
  }
}
</style>
